import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  frequencyOptions,
  proficiencyOptions,
  handicapOptions,
} from './constants'
import { handednessOptions } from 'utils/constants'

const useRadioOptionStyles = makeStyles(theme => ({
  optionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  optionLabel: {
    alignItems: 'center',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(0.5),
    },
  },
  optionLabelMultiline: {
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(0.5),
    },
  },
  optionTitle: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
  },
  optionSupport: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  radio: {
    padding: theme.spacing(1),
    color: '#70787C',
    '&.Mui-checked': {
      color: '#0053FF',
    },
  },
}))

const RadioQuestion = ({
  name,
  heading,
  options,
  control,
  error,
  renderLabel,
  multilineLabel = false,
  'data-cy': dataCy,
}) => {
  const classes = useStyles()
  const radioClasses = useRadioOptionStyles()
  const labelClassName = multilineLabel
    ? radioClasses.optionLabelMultiline
    : radioClasses.optionLabel

  return (
    <Box className={classes.fieldGroup}>
      <Typography className={classes.sectionHeading}>{heading}</Typography>
      <Controller
        name={name}
        control={control}
        render={({ value, onChange }) => (
          <RadioGroup
            className={radioClasses.optionsColumn}
            value={value ?? ''}
            onChange={event => onChange(event.target.value)}
            data-cy={dataCy}
          >
            {options.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                className={labelClassName}
                data-cy={dataCy ? `${dataCy}-${option.value}` : undefined}
                control={
                  <Radio className={radioClasses.radio} color="primary" />
                }
                label={
                  renderLabel ? (
                    renderLabel(option, radioClasses)
                  ) : (
                    <Typography className={radioClasses.optionTitle}>
                      {option.label}
                    </Typography>
                  )
                }
              />
            ))}
          </RadioGroup>
        )}
      />
      {error ? (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      ) : null}
    </Box>
  )
}

const GolfInformation = () => {
  const classes = useStyles()
  const { control, errors, register, watch } = useFormContext()
  const courseAssociatedWatch = watch('golfInformation.associatedWithCourse')
  const golfErrors = errors?.golfInformation ?? {}

  React.useEffect(() => {
    if (golfErrors?.homeGolfCourse) {
      document
        .getElementById('golf-information-club-name')
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [golfErrors?.homeGolfCourse])

  const proficiencyLabel = (option, radioClasses) => (
    <Box>
      <Typography className={radioClasses.optionTitle} component="span">
        {option.label}
      </Typography>
      {': '}
      <Typography className={radioClasses.optionSupport} component="span">
        {option.supportText}
      </Typography>
    </Box>
  )

  return (
    <Box>
      <RadioQuestion
        name="golfInformation.frequency"
        heading="How often do you play, practice or work with a coach?"
        options={frequencyOptions}
        control={control}
        error={golfErrors?.frequency?.message}
        data-cy="frequencyOption"
      />
      <RadioQuestion
        name="golfInformation.associatedWithCourse"
        heading="Are you associated with a specific club or course?"
        options={[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]}
        control={control}
        error={golfErrors?.associatedWithCourse?.message}
        data-cy="courseAssociationOption"
      />
      {courseAssociatedWatch === 'true' ? (
        <Box className={classes.fieldGroup} id="golf-information-club-name">
          <TextField
            name="golfInformation.homeGolfCourse"
            inputRef={register}
            error={!!golfErrors?.homeGolfCourse}
            helperText={golfErrors?.homeGolfCourse?.message}
            label="Club or course name"
            variant="outlined"
            fullWidth
            className={classes.fieldInput}
          />
        </Box>
      ) : null}
      <RadioQuestion
        name="golfInformation.proficiency"
        heading="Where are you in your golfer journey?"
        options={proficiencyOptions}
        control={control}
        error={golfErrors?.proficiency?.message}
        renderLabel={proficiencyLabel}
        multilineLabel
        data-cy="golfJourneyOption"
      />
      <RadioQuestion
        name="golfInformation.ownClubs"
        heading="Do you plan to bring your own clubs?"
        options={[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]}
        control={control}
        error={golfErrors?.ownClubs?.message}
        data-cy="bringOwnClubsOption"
      />
      <RadioQuestion
        name="golfInformation.handedness"
        heading="What is your handedness?"
        options={handednessOptions}
        control={control}
        error={golfErrors?.handedness?.message}
        data-cy="handednessOption"
      />
      <RadioQuestion
        name="golfInformation.handicapRating"
        heading="What is your handicap?"
        options={handicapOptions}
        control={control}
        error={golfErrors?.handicapRating?.message}
        data-cy="handicapOption"
      />
    </Box>
  )
}

export default GolfInformation
